import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon, CheckIcon, XIcon, StarIcon, TrendingUpIcon } from 'lucide-react';
import { TopBar } from '../components/TopBar';
interface PlanSelectionProps {
  navigate: (screen: string) => void;
}
export function PlanSelection({
  navigate
}: PlanSelectionProps) {
  const [showModal, setShowModal] = useState(false);
  const plans = [{
    id: 'basic',
    name: 'Plan A',
    type: 'Standard',
    premium: '₦15,500',
    returns: '8% Projected',
    features: ['Basic Life Cover', 'Standard Growth', 'Annual Statements'],
    popular: false
  }, {
    id: 'plus',
    name: 'Plan B',
    type: 'Plus',
    premium: '₦16,666',
    returns: '10% Projected',
    features: ['Enhanced Life Cover', 'Premium Growth', 'Quarterly Statements', 'Critical Illness Add-on'],
    popular: true
  }, {
    id: 'premium',
    name: 'Plan C',
    type: 'Premium',
    premium: '₦18,200',
    returns: '12% Projected',
    features: ['Maximum Life Cover', 'Aggressive Growth', 'Monthly Statements', 'Critical Illness Add-on', 'Premium Waiver'],
    popular: false
  }];
  // Generate confetti pieces for the modal
  const confetti = Array.from({
    length: 30
  }).map((_, i) => ({
    id: i,
    x: Math.random() * 200 - 100,
    y: Math.random() * -200 - 50,
    rotation: Math.random() * 360,
    scale: Math.random() * 0.5 + 0.5,
    color: ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6'][Math.floor(Math.random() * 4)]
  }));
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="flex-1 overflow-y-auto pb-24 bg-slate-50 relative" data-id="element-586">
      <TopBar title="Choose Your Plan" showBack onBack={() => navigate('product-detail')} data-id="element-587" />

      <div className="px-5 pt-4 pb-24" data-id="element-588">
        <div className="mb-8" data-id="element-589">
          <h1 className="text-2xl font-bold text-[#1B3B36] mb-2" data-id="element-590">
            Choose Your Plan
          </h1>
          <p className="text-sm text-gray-500" data-id="element-591">
            Select a plan that best fits your child's education goals.
          </p>
        </div>

        <div className="space-y-6" data-id="element-592">
          {plans.map((plan, idx) => <motion.div key={plan.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: idx * 0.1
        }} className={`relative bg-white rounded-3xl p-6 border-2 transition-all ${plan.popular ? 'border-amber-500 shadow-xl shadow-amber-500/10 scale-[1.02]' : 'border-gray-100 shadow-sm'}`} data-id="element-593">
              {plan.popular && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-amber-500 text-[#1B3B36] px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-sm" data-id="element-594">
                  <StarIcon className="w-3.5 h-3.5 fill-[#1B3B36]" data-id="element-595" /> Most
                  Popular
                </div>}

              <div className="flex justify-between items-start mb-6" data-id="element-596">
                <div data-id="element-597">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-598">
                    {plan.type}
                  </p>
                  <h3 className="text-xl font-bold text-[#1B3B36]" data-id="element-599">
                    {plan.name}
                  </h3>
                </div>
                <div className="text-right" data-id="element-600">
                  <p className="text-2xl font-bold text-[#1B3B36]" data-id="element-601">
                    {plan.premium}
                  </p>
                  <p className="text-xs text-gray-500 font-medium" data-id="element-602">/month</p>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-xl p-3 mb-6 flex items-center gap-3 border border-emerald-100" data-id="element-603">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0" data-id="element-604">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-605" />
                </div>
                <div data-id="element-606">
                  <p className="text-xs font-bold text-emerald-800" data-id="element-607">
                    Expected Returns
                  </p>
                  <p className="text-sm font-bold text-emerald-600" data-id="element-608">
                    {plan.returns}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-8" data-id="element-609">
                {plan.features.map((feature, fIdx) => <div key={fIdx} className="flex items-start gap-3" data-id="element-610">
                    <CheckIcon className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-611" />
                    <span className="text-sm text-gray-600 font-medium" data-id="element-612">
                      {feature}
                    </span>
                  </div>)}
              </div>

              <button onClick={() => setShowModal(true)} className={`w-full py-4 rounded-xl font-bold text-sm transition-all active:scale-95 ${plan.popular ? 'bg-[#1B3B36] text-white shadow-lg shadow-emerald-900/20' : 'bg-gray-50 text-[#1B3B36] hover:bg-gray-100 border border-gray-200'}`} data-id="element-613">
                Select {plan.name}
              </button>
            </motion.div>)}
        </div>
      </div>

      {/* Confirmation Modal */}
      <AnimatePresence data-id="element-614">
        {showModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowModal(false)} className="fixed inset-0 bg-[#1B3B36]/60 backdrop-blur-sm z-50" data-id="element-615" />

            <motion.div initial={{
          opacity: 0,
          y: '100%'
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-[2rem] p-6 pb-safe" data-id="element-616">
              <div className="flex justify-between items-center mb-6" data-id="element-617">
                <h3 className="text-xl font-bold text-[#1B3B36]" data-id="element-618">
                  Confirm Selection
                </h3>
                <button onClick={() => setShowModal(false)} className="p-2 bg-gray-100 rounded-full text-gray-500 hover:bg-gray-200" data-id="element-619">
                  <XIcon className="w-5 h-5" data-id="element-620" />
                </button>
              </div>

              <div className="bg-gray-50 rounded-2xl p-5 mb-6 border border-gray-100" data-id="element-621">
                <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-200" data-id="element-622">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600" data-id="element-623">
                    <ShieldCheckIcon className="w-6 h-6" data-id="element-624" />
                  </div>
                  <div data-id="element-625">
                    <h4 className="font-bold text-[#1B3B36]" data-id="element-626">Plan B (Plus)</h4>
                    <p className="text-sm text-gray-500" data-id="element-627">Child Education</p>
                  </div>
                </div>
                <div className="flex justify-between items-center" data-id="element-628">
                  <span className="text-sm font-medium text-gray-500" data-id="element-629">
                    Monthly Premium
                  </span>
                  <span className="text-lg font-bold text-[#1B3B36]" data-id="element-630">
                    ₦16,666
                  </span>
                </div>
              </div>

              <p className="text-xs text-gray-500 text-center mb-6 leading-relaxed px-4" data-id="element-631">
                By proceeding, you agree to the terms and conditions. You will
                be redirected to complete your KYC and make the first payment.
              </p>

              <button onClick={() => {
            setShowModal(false);
            navigate('wallet');
          }} className="w-full bg-amber-500 hover:bg-amber-400 text-[#1B3B36] font-bold py-4 rounded-xl shadow-lg shadow-amber-500/30 active:scale-95 transition-transform" data-id="element-632">
                Proceed to Payment
              </button>
            </motion.div>
          </>}
      </AnimatePresence>
    </motion.div>;
}